import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }

    // Token-validation must be a GET (the backend treats POST as the actual
    // unsubscribe action). supabase.functions.invoke() always POSTs, so use a
    // raw fetch with the anon key — this matches the public-token contract.
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
    fetch(`${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`, {
      headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.valid === false && data?.reason === "already_unsubscribed") setStatus("already");
        else if (data?.valid) setStatus("valid");
        else setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (error) throw error;
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <>
      <SEO
        title="Unsubscribe — HeirWise Email Preferences"
        description="Manage your HeirWise email preferences and unsubscribe from product updates. Essential account emails will still be delivered."
        path="/unsubscribe"
      />
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-md w-full text-center space-y-6">
        <h1 className="text-2xl font-heading font-bold text-foreground">Email Preferences</h1>

        {status === "loading" && <p className="text-muted-foreground">Verifying…</p>}

        {status === "valid" && (
          <>
            <p className="text-muted-foreground">
              Click below to unsubscribe from HeirWise app emails. You will still receive
              essential account emails like password resets.
            </p>
            <button
              onClick={handleConfirm}
              className="px-6 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:opacity-90 transition"
            >
              Confirm Unsubscribe
            </button>
          </>
        )}

        {status === "success" && (
          <p className="text-green-600 dark:text-green-400">
            You have been unsubscribed. You won't receive app emails from HeirWise anymore.
          </p>
        )}

        {status === "already" && (
          <p className="text-muted-foreground">You're already unsubscribed from these emails.</p>
        )}

        {status === "invalid" && (
          <p className="text-destructive">This unsubscribe link is invalid or has expired.</p>
        )}

        {status === "error" && (
          <p className="text-destructive">Something went wrong. Please try again later.</p>
        )}
      </div>
      </div>
    </>
  );
};

export default Unsubscribe;
